import type { ReactElement, ReactNode } from 'react';
import { Check } from 'lucide-react';
import type { ToolApprovalScope } from '@kirin/api-client/contracts/shadow-ai-tool-approvals';
import type { BulkToolbarContext } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SUPPORT_READONLY_TOOLTIP } from '@/components/alertActionTooltipCopy';
import { cn } from '@/lib/utils';
import {
  useToolApprovalMutation,
  type ToolApprovalAction,
} from '@/hooks/api/useShadowAiToolApprovals';
import {
  getToolApprovalBulkIds,
  type ToolApprovalBulkRow,
} from './toolApprovalBulkIds';

export interface ToolApprovalBulkBarProps<TRow extends ToolApprovalBulkRow> {
  ctx: BulkToolbarContext<TRow>;
  organizationId: string;
  scope: ToolApprovalScope;
  getToolId: (row: TRow) => string;
  /** Support: render the actions but disabled ("disable, don't hide"). */
  disabled?: boolean;
}

function SupportDisabledAction({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): ReactElement {
  // A native `disabled` button swallows hover/focus, so the tooltip would
  // never open — mirrors the Alerts bulk bar's support branch.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex">
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-disabled="true"
            className={cn('pointer-events-none opacity-50', className)}
            onClick={event => event.preventDefault()}
          >
            {children}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{SUPPORT_READONLY_TOOLTIP}</TooltipContent>
    </Tooltip>
  );
}

/** `Approve (2)`; the bare label when nothing applies (button disabled). */
function withCount(label: string, count: number): string {
  return count > 0 ? `${label} (${count})` : label;
}

const APPROVE_CLASS =
  'h-7 gap-1 border-emerald-600 bg-emerald-600 text-white shadow-none hover:bg-emerald-700 hover:text-white';
const UNAPPROVE_CLASS = 'h-7 bg-background shadow-none';

/**
 * KIR-4855: bulk Approve / Unapprove bar shown below the rows of the Shadow AI
 * Tools tables while one or more rows are selected. Renders nothing with no
 * selection.
 */
export function ToolApprovalBulkBar<TRow extends ToolApprovalBulkRow>({
  ctx,
  organizationId,
  scope,
  getToolId,
  disabled = false,
}: ToolApprovalBulkBarProps<TRow>): ReactElement | null {
  const mutation = useToolApprovalMutation(organizationId);

  const selectedCount = ctx.selectedRowIds.length;
  if (selectedCount === 0) return null;

  const ids = getToolApprovalBulkIds(ctx.selectedRows, getToolId);

  const run = (action: ToolApprovalAction): void => {
    const toolIds = action === 'approve' ? ids.approve : ids.unapprove;
    if (toolIds.length === 0) return;
    mutation.mutate(
      { action, scope, toolIds },
      { onSuccess: () => ctx.clearSelection() }
    );
  };

  // Rows ≠ tools: several rows can share a tool, and a mixed selection only
  // partly applies to each action, so each button shows how many tools it
  // will change.
  const approveLabel = (
    <>
      <Check className="h-3.5 w-3.5" aria-hidden="true" />
      {withCount('Approve', ids.approve.length)}
    </>
  );
  const unapproveLabel = withCount('Unapprove', ids.unapprove.length);

  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-[#dde6f7] bg-[#eef2fb] px-4 py-1 text-sm dark:border-[#1e2a44] dark:bg-[#11192e]"
      data-testid="tool-approval-bulk-bar"
    >
      <span className="font-medium">{selectedCount} selected</span>

      {disabled ? (
        <>
          <SupportDisabledAction className={APPROVE_CLASS}>
            {approveLabel}
          </SupportDisabledAction>
          <SupportDisabledAction className={UNAPPROVE_CLASS}>
            {unapproveLabel}
          </SupportDisabledAction>
        </>
      ) : (
        <>
          <Button
            type="button"
            size="sm"
            disabled={mutation.isPending || ids.approve.length === 0}
            aria-busy={mutation.isPending}
            onClick={() => run('approve')}
            className={APPROVE_CLASS}
          >
            {approveLabel}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={mutation.isPending || ids.unapprove.length === 0}
            aria-busy={mutation.isPending}
            onClick={() => run('unapprove')}
            className={UNAPPROVE_CLASS}
          >
            {unapproveLabel}
          </Button>
        </>
      )}

      <Button
        type="button"
        variant="link"
        size="sm"
        onClick={ctx.clearSelection}
        className="h-auto p-0 text-sm font-medium text-blue-500 dark:text-blue-400"
      >
        Clear selection
      </Button>
    </div>
  );
}
